import { proposalsService } from '@/services';
import type {
  GetProposalsParams,
  ProposalsResponse,
} from '@/services/proposals/types';
import { PortalStatus } from '@/shared/enums/portal-status.enum';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

const CANCELLED_STATUS = String(PortalStatus.ProposalCancelled);

type CountScope = {
  partyCode?: string;
  productCode?: string;
  proposalDateFrom?: string;
  proposalDateTo?: string;
};

function sameScope(a: CountScope, b: CountScope): boolean {
  return (
    a.partyCode === b.partyCode &&
    a.productCode === b.productCode &&
    a.proposalDateFrom === b.proposalDateFrom &&
    a.proposalDateTo === b.proposalDateTo
  );
}

function filterIncludes(status: string | undefined, code: string): boolean {
  if (!status) return true;
  return status.split(',').includes(code);
}

/**
 * A listagem lê o índice do InsureMO (`queryPolicy`), que ainda devolve o
 * status anterior logo depois do `updateEx`. Um refetch imediato grava esse
 * retorno no cache e a tag só muda no F5. O cache da lista é atualizado na
 * hora, sem esperar o índice.
 */
function applyCancelledProposal(
  queryClient: QueryClient,
  policyId: string
): { previousStatus?: string; scopes: CountScope[] } {
  let previousStatus: string | undefined;
  const scopes: CountScope[] = [];

  const lists = queryClient.getQueriesData<ProposalsResponse>({
    queryKey: ['proposals'],
  });

  for (const [key, current] of lists) {
    if (!current?.data) continue;
    const row = current.data.find(item => item.id === policyId);
    if (!row) continue;

    previousStatus ??= row.proposalStatus;
    const params = (key[1] ?? {}) as GetProposalsParams;
    const scope = {
      partyCode: params.partyCode,
      productCode: params.productCode,
      proposalDateFrom: params.proposalDateFrom,
      proposalDateTo: params.proposalDateTo,
    };
    if (!scopes.some(existing => sameScope(existing, scope))) {
      scopes.push(scope);
    }

    if (filterIncludes(params.status, CANCELLED_STATUS)) {
      queryClient.setQueryData<ProposalsResponse>(key, {
        ...current,
        data: current.data.map(item =>
          item.id === policyId
            ? { ...item, proposalStatus: CANCELLED_STATUS }
            : item
        ),
      });
      continue;
    }

    const total = Math.max(0, (current.meta?.total ?? current.data.length) - 1);
    const pageSize = current.meta?.pageSize || 1;
    queryClient.setQueryData<ProposalsResponse>(key, {
      ...current,
      data: current.data.filter(item => item.id !== policyId),
      meta: {
        ...current.meta,
        total,
        totalPages: total === 0 ? 0 : Math.ceil(total / pageSize),
      },
    });
  }

  return { previousStatus, scopes };
}

function adjustStatusCounts(
  queryClient: QueryClient,
  previousStatus: string,
  scopes: CountScope[]
) {
  if (previousStatus === CANCELLED_STATUS) return;

  const queries = queryClient.getQueriesData<number>({
    queryKey: ['proposals-status-count'],
  });

  for (const [key, total] of queries) {
    if (typeof total !== 'number') continue;
    const [, status, partyCode, productCode, proposalDateFrom, proposalDateTo] =
      key as [string, string, string?, string?, string?, string?];
    const matchesScope = scopes.some(scope =>
      sameScope(scope, {
        partyCode,
        productCode,
        proposalDateFrom,
        proposalDateTo,
      })
    );
    if (!matchesScope) continue;

    const codes = String(status ?? '')
      .split(',')
      .filter(Boolean);
    if (!codes.length) continue;

    const hadPrevious = codes.includes(previousStatus);
    const hasCancelled = codes.includes(CANCELLED_STATUS);
    if (hadPrevious && !hasCancelled) {
      queryClient.setQueryData(key, Math.max(0, total - 1));
    } else if (hasCancelled && !hadPrevious) {
      queryClient.setQueryData(key, total + 1);
    }
  }
}

export function useCancelProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['proposals', 'cancel'],
    mutationFn: async (policyId: string) => {
      const response = await proposalsService.cancelProposal(policyId);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao cancelar a proposta');
      }
      return response.data;
    },
    onSuccess: async (_result, policyId) => {
      await queryClient.cancelQueries({ queryKey: ['proposals'] });
      await queryClient.cancelQueries({ queryKey: ['proposals-status-count'] });
      const { previousStatus, scopes } = applyCancelledProposal(
        queryClient,
        policyId
      );
      if (previousStatus) {
        adjustStatusCounts(queryClient, previousStatus, scopes);
      }
    },
  });
}
